import { ReactTFC } from "../../utils/fp/missing-ts-parts";
import { PlpSearchResponse, type TranslatedValue } from "@swo/storefront";
import React from "react";

import { Spacer } from "../../components/_shared-components/arrangement/Space";
import { ProductListingPageFilter } from "../../components/_shared-components/generic-listing-page/ProductListingPageFilter";
import { ProductListingPageTiles, PLPTile } from "./ProductListingPageFamiliesTiles/ProductListingPageFilter";
import { ActivatedFacets } from "../../components/_shared-components/generic-listing-page/types";
import { PlpSitemapLinker } from "./sitemap/plp-sitemap";
import { LoadingStateWithIdle, LoadingStateWithIdleOps } from "../../domain/data-layer/loading-state-with-idle";
import { LoadingState, LoadingStateOps } from "../../domain/data-layer/loading-state";
import { SwarovskiOptikSegmentShort, showPrice } from "@swo/shared";
import { useNavigate } from "react-router";
import { usePageContext } from "../../global/page-context";
import { PlpRootLevelProps } from "./levels/0-root-level";
import { getActivatedFacets } from "./io/getActivatedFacets";

type QueryResponse = LoadingState<{
  result: PlpSearchResponse;
  version: { subpage: PlpRootLevelProps["subpage"] };
}>;

export const ProductListingPageDataLayout: ReactTFC<{
  tiles: PlpRootLevelProps["data"]["tiles"];
  isRefetching: boolean;
  storefrontResponseWithStalenessLS: QueryResponse;
  sitemapLinker: PlpSitemapLinker;
  isSparePartsPlp: boolean;
  colorsCssMap: Record<string, string>;
  label: TranslatedValue;
  page: PlpRootLevelProps["page"];
  segment: SwarovskiOptikSegmentShort;
}> = ({
  page,
  segment,
  sitemapLinker,
  colorsCssMap,
  isSparePartsPlp,
  label,
  isRefetching,
  storefrontResponseWithStalenessLS,
  tiles,
}) => {
  const navigate = useNavigate();
  const localeForNumberFormat = usePageContext().pageBase.locale;

  const loadingState = LoadingStateOps.map(storefrontResponseWithStalenessLS, _ => ({
    products: _.result.products,
    activatedFacets: getActivatedFacets(_.version.subpage),
    subpageTag: _.version.subpage.tag,
    openedFilterTabByFilterKey: _.version.subpage.openedFilterTabByFilterKey,
    facets: _.result.facets,
  }));

  const rootTiles = tiles.map(t => {
    return {
      ...t,
      action: {
        tag: "click" as const,
        onClick: () => {
          void navigate(
            isSparePartsPlp
              ? {
                  ...sitemapLinker("category", {
                    categoryKey: t.key,
                  }),
                }
              : {
                  ...sitemapLinker("facetedSearch", {
                    activatedFacets: { family: [t.key] },
                    openedFilterTabByFilterKey: undefined,
                  }),
                },
            {
              replace: true,
            },
          );

          window.scrollTo({ top: 0, behavior: "smooth" });
        },
      },
    };
  });

  function getContentBySearchMode(searchData: PlpSearchResponse["products"]): Array<PLPTile> {
    return searchData.map(p => {
      return {
        id: p.__key,
        headline: p.name,
        isNew: p.isNew,
        subheadlines: [],
        asset: p.image?.url,
        backgroundUrl: p.hoverImage?.url || p.image?.url,
        text: p.lowestPrice ? showPrice(p.lowestPrice, localeForNumberFormat) : undefined,
        action: {
          tag: "link" as const,
          href: {
            pathname: p.link,
            search: undefined,
          },
          metadata: {
            productName: p.name,
            currencyCode: p.lowestPrice?.currencyCode || "-",
            category: page.categoryKey,
            segmentKey: segment.key,
          },
        },
      };
    });
  }

  const content =
    loadingState.__tag === "loaded"
      ? loadingState.data.subpageTag === "root"
        ? rootTiles
        : getContentBySearchMode(loadingState.data.products)
      : null;

  return (
    <>
      {!isSparePartsPlp && (
        <ProductListingPageFilter
          isRefetching={isRefetching}
          loadingState={LoadingStateOps.map(
            loadingState,
            ({ activatedFacets, openedFilterTabByFilterKey, facets }) => ({
              activatedFacets,
              openedFilterTabByFilterKey,
              facets,
            }),
          )}
          sitemapLinker={sitemapLinker}
          colorsCssMap={colorsCssMap}
          inputType="radio"
          label={label}
        />
      )}

      <Spacer size={"md"} />

      {content ? <ProductListingPageTiles isRefetching={isRefetching} tiles={content} /> : null}
    </>
  );
};
